import { computed, unref, type ComputedRef } from 'vue'

import { useMutation, useQuery } from '@tanstack/vue-query'

import { queryClient } from '@/lib/queryClient'

import { handleApiErrorToast } from '..'
import { adminQueryKeys } from './useAdminQueries'
import { GptPublicLinkApi } from '../modules/gptPublicLink'

import type { IGptPublicLink } from '../types/publicGpt'
import type { AxiosError } from 'axios'

const publicLinkKey = (gptId: string | undefined) =>
  ['gpts', 'public-link', gptId] as const

/**
 * TanStack-query hooks for admin management of a GPT's public share link.
 * Modeled on {@link useGptQueries}. The `status` query intentionally uses
 * `retry: false` and does NOT toast — a 404 is the expected "no link yet" state
 * that the modal branches on.
 */
export const useGptPublicLinkQueries = () => {
  const status = (gptId: ComputedRef<string | undefined>) =>
    useQuery<IGptPublicLink, AxiosError>({
      queryKey: computed(() => publicLinkKey(unref(gptId))),
      queryFn: () => {
        const id = unref(gptId)
        if (!id) throw new Error('gptId is required')
        return GptPublicLinkApi.get(id)
      },
      enabled: computed(() => !!unref(gptId)),
      retry: false,
      staleTime: 0,
    })

  /**
   * Refresh both the link record and the GPT lists: the card's "Published" tag
   * reads `is_web_published` off the list payload, so a publish/unpublish has to
   * refetch the lists too. `['gpts']` is a prefix of `publicLinkKey`, so this
   * also invalidates the link record itself.
   */
  const invalidatePublishState = () => {
    queryClient.invalidateQueries({ queryKey: ['gpts'] })
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
  }

  const create = useMutation({
    mutationFn: (gptId: string) => GptPublicLinkApi.create(gptId),
    onSuccess: invalidatePublishState,
    onError: handleApiErrorToast,
  })

  const setActive = useMutation({
    mutationFn: ({ gptId, isActive }: { gptId: string; isActive: boolean }) =>
      GptPublicLinkApi.setActive(gptId, isActive),
    onSuccess: invalidatePublishState,
    onError: handleApiErrorToast,
  })

  return { status, create, setActive }
}
